import { useState } from "react";
import { useSessionStore } from "../store/sessionStore";
import { useAuthStore } from "../store/authStore";
import { useTemplateStore } from "../store/templateStore";
import { newSession, type Session } from "../lib/session";

export function ChannelSidebar() {
  const { channels, activeChannelId, createChannel, addChannel, selectChannel, deleteChannel, togglePinned, toggleArchived, duplicateChannel } =
    useSessionStore();
  const creatorName = useAuthStore((s) => s.user?.name ?? "anon");
  const { templates, defaultId } = useTemplateStore();
  const [showArchived, setShowArchived] = useState(false);

  // Filter out archived channels by default; show them under a
  // small toggle when the user wants to find one.
  const visibleChannels = showArchived
    ? channels.filter((c) => c.archived)
    : channels.filter((c) => !c.archived);
  const archivedCount = channels.filter((c) => c.archived).length;

  // Stable sort: pinned first (preserve activity order within each
  // group). Activity order is the input order — sessionStore puts
  // recently-appended channels at the head.
  const sortedChannels = [...visibleChannels].sort((a, b) => {
    const ap = a.pinned ? 1 : 0;
    const bp = b.pinned ? 1 : 0;
    return bp - ap;
  });
  const [creating, setCreating] = useState(false);
  const [draftName, setDraftName] = useState("");

  function commitNew() {
    const title = draftName.trim();
    if (!title) {
      setCreating(false);
      return;
    }
    // If a default template is set and still exists, spawn the new
    // channel with that shape instead of blank. Falls back to the
    // existing blank-channel flow when there's no default or the
    // default points at a deleted template.
    const tpl = defaultId ? templates.find((t) => t.id === defaultId) : null;
    if (tpl) {
      const seed = newSession(title, creatorName);
      const fresh: Session = {
        ...seed,
        mode: tpl.mode,
        document: tpl.document,
        attached_documents: tpl.attached_documents,
        brainstorm_mp_enabled: tpl.brainstorm_mp_enabled,
        tools_enabled: tpl.tools_enabled,
        provider_preference: tpl.provider_preference ?? seed.provider_preference,
        mosh_pit_enabled: tpl.mosh_pit_enabled ?? seed.mosh_pit_enabled,
      };
      addChannel(fresh);
    } else {
      createChannel(title, creatorName);
    }
    setDraftName("");
    setCreating(false);
  }

  function cancelNew() {
    setDraftName("");
    setCreating(false);
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-head">
        <span className="brand-dot" />
        <span className="sidebar-brand">channels</span>
        <button
          className="btn-ghost sidebar-new"
          onClick={() => setCreating(true)}
          title="New channel"
          aria-label="New channel"
        >
          +
        </button>
      </div>

      <div className="channel-list">
        {creating && (
          <div className="channel-row creating">
            <input
              autoFocus
              type="text"
              value={draftName}
              placeholder="channel name…"
              onChange={(e) => setDraftName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitNew();
                if (e.key === "Escape") cancelNew();
              }}
              onBlur={commitNew}
            />
          </div>
        )}

        {channels.length === 0 && !creating && (
          <div className="sidebar-empty">
            No channels yet. <button className="link-btn" onClick={() => setCreating(true)}>
              Create one
            </button>.
          </div>
        )}

        {sortedChannels.map((c) => (
          <ChannelRow
            key={c.id}
            channel={c}
            active={c.id === activeChannelId}
            onSelect={() => selectChannel(c.id)}
            onTogglePin={() => togglePinned(c.id)}
            onToggleArchive={() => toggleArchived(c.id)}
            onDuplicate={() => duplicateChannel(c.id)}
            onDelete={() => {
              if (confirm(`Delete channel "${c.title}"? Archive it first if you might want it back later.`)) {
                deleteChannel(c.id);
              }
            }}
          />
        ))}
      </div>

      {(archivedCount > 0 || showArchived) && (
        <div className="sidebar-archive-toggle">
          <button
            className="link-btn"
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived
              ? `← back to active`
              : `show ${archivedCount} archived`}
          </button>
        </div>
      )}
    </aside>
  );
}

interface ChannelRowProps {
  channel: Session;
  active: boolean;
  onSelect: () => void;
  onTogglePin: () => void;
  onToggleArchive: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function ChannelRow({ channel, active, onSelect, onTogglePin, onToggleArchive, onDuplicate, onDelete }: ChannelRowProps) {
  const lastMessage = channel.messages[channel.messages.length - 1];
  const preview = lastMessage
    ? truncate(lastMessage.content, 48)
    : "no messages yet";

  return (
    <div
      className={`channel-row ${active ? "active" : ""}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="channel-row-main">
        <div className="channel-title">
          {channel.title}
          {channel.mode === "brainstorm" && (
            <span className="channel-badge" title="Brainstorm mode — Ask rewrites the whiteboard">
              brainstorm
            </span>
          )}
        </div>
        <div className="channel-preview">{preview}</div>
      </div>
      <button
        className={`channel-pin ${channel.pinned ? "pinned" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          onTogglePin();
        }}
        title={channel.pinned ? "Unpin from top" : "Pin to top"}
        aria-label={channel.pinned ? `Unpin channel ${channel.title}` : `Pin channel ${channel.title}`}
      >
        {channel.pinned ? "★" : "☆"}
      </button>
      <button
        className="channel-dup"
        onClick={(e) => {
          e.stopPropagation();
          onDuplicate();
        }}
        title="Duplicate channel — keeps history; new id, no sync"
        aria-label={`Duplicate channel ${channel.title}`}
      >
        ⎘
      </button>
      <button
        className="channel-archive"
        onClick={(e) => {
          e.stopPropagation();
          onToggleArchive();
        }}
        title={channel.archived ? "Unarchive (return to active list)" : "Archive (hide from sidebar; keep everything)"}
        aria-label={channel.archived ? `Unarchive channel ${channel.title}` : `Archive channel ${channel.title}`}
      >
        {channel.archived ? "↺" : "▭"}
      </button>
      <button
        className="channel-delete"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        title="Delete channel"
        aria-label={`Delete channel ${channel.title}`}
      >
        ×
      </button>
    </div>
  );
}

function truncate(s: string, max: number): string {
  const oneLine = s.replace(/\s+/g, " ").trim();
  if (oneLine.length <= max) return oneLine;
  return oneLine.slice(0, max - 1) + "…";
}
